import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid, Line, LineChart, ReferenceArea, ReferenceDot, ReferenceLine,
  ResponsiveContainer, XAxis, YAxis,
} from 'recharts';
import { LAP_COLORS } from '../LapSelector/LapSelector';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { subscribeExtent } from '../../utils/chartExtentBus';
import { publishZoom, subscribeZoom } from '../../utils/chartZoomBus';
import { lastCursor } from '../../utils/chartCursor';
import { convertDistanceFt, getDistanceUnit, TOOLTIP_FONT_SCALES } from '../../utils/unitConvert';
import { useCornerHighlight } from '../../hooks/useCornerHighlight';
import { usePanOnWheel } from '../../hooks/usePanOnWheel';
import { useTouchChartGestures } from '../../hooks/useTouchChartGestures';
import { useTheme } from '../../contexts/ThemeContext';
import { assessSharedScale, domainForSeries, niceTicks } from '../../utils/channelScale';
import { exportTracePanelPng } from '../../utils/traceExport';
import { formatLapTimestamp } from '../../utils/traceTime';
import {
  effectivePanelColorMode,
  MAX_EXTRA_CHANNELS,
  MAX_PANEL_CHANNELS,
  partitionPanelChannelOptions,
} from '../../utils/channelPanelConfigs';
import styles from './GTrace.module.css';
import pickerStyles from './SpeedTrace.module.css';
import { arrayExtent } from '../../utils/extent';

const M_TO_FT = 3.28084;
const CHART_TOP = 8;
const CHART_BOTTOM = 22;
const Y_AXIS_W = 44;
const RIGHT_MARGIN = 12;
// Pointer travel (px) before a mousedown is treated as a zoom drag rather than
// a click. Below it the chart must not re-render, or React swaps the node the
// mousedown landed on and the browser never fires the click.
const DRAG_ARM_PX = 4;
const SPLIT_DIVIDER_H = 1;
const MIN_SUB_H = 60;
const CHANNEL_COLORS = [
  '#e8411a', '#4da6ff', '#44cc88', '#f5c842',
  '#cc66ff', '#22c7d6', '#ff7aaf', '#a6d854',
];
const CHANNEL_LAP_OPACITIES = [1, 0.8, 0.64, 0.5, 0.9, 0.72, 0.57, 0.44, 0.84];
// Zero-split handle travel limits (fraction of the base signal's band height).
const ZERO_FRAC_MIN = 0.08;
const ZERO_FRAC_MAX = 0.92;

const clamp = (value, low, high) => Math.max(low, Math.min(high, value));

function downsample(points, maxPoints) {
  if (points.length <= maxPoints) return points;
  const step = points.length / maxPoints;
  return Array.from({ length: maxPoints }, (_, index) => points[Math.round(index * step)]);
}

function findNearest(points, distFt) {
  if (!points?.length) return null;
  let best = points[0];
  let bestDiff = Math.abs(best.dist_ft - distFt);
  for (let index = 1; index < points.length; index += 1) {
    const diff = Math.abs(points[index].dist_ft - distFt);
    if (diff < bestDiff) {
      best = points[index];
      bestDiff = diff;
    }
  }
  return best;
}

// Value of the drawn line at `distFt`: linear interpolation between the two
// samples that bracket it (points are ascending in dist_ft). The nearest sample
// alone can sit half a sample-spacing away, which on a steep slope zoomed in
// puts the hover dot visibly off the curve Recharts interpolates between them.
function valueAt(points, distFt) {
  if (!points?.length) return null;
  let low = 0;
  let high = points.length - 1;
  if (distFt <= points[low].dist_ft) return points[low].value;
  if (distFt >= points[high].dist_ft) return points[high].value;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (points[mid].dist_ft <= distFt) low = mid; else high = mid;
  }
  const a = points[low];
  const b = points[high];
  if (a.value == null || b.value == null) return findNearest(points, distFt)?.value ?? null;
  const span = b.dist_ft - a.dist_ft;
  if (!(span > 0)) return a.value;
  return a.value + (b.value - a.value) * ((distFt - a.dist_ft) / span);
}

function fmtValue(value) {
  if (value == null) return '—';
  const abs = Math.abs(value);
  if (abs >= 1000) return Math.round(value).toLocaleString();
  if (abs >= 100) return Math.round(value).toString();
  if (abs >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function fmtAxis(value) {
  if (value == null || !Number.isFinite(value)) return '';
  const abs = Math.abs(value);
  if (abs >= 10000) return `${(value / 1000).toFixed(0)}k`;
  if (abs >= 1000) return `${(value / 1000).toFixed(1)}k`;
  if (abs >= 100) return Math.round(value).toString();
  if (abs >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function buildSeries(signal, customColors1, zoomDomain) {
  const build = (
    lapTraces, selectedLaps, palette, prefix, customColors,
    valueTransform, yLabel, nativeUnit, source,
  ) => (
    selectedLaps.map((lapIndex, colorIndex) => {
      const trace = lapTraces?.[lapIndex];
      if (!trace) return null;
      const raw = (trace.points || []).filter(point => (
        point.dist_m != null && point.lat && point.lon && point.channel_value != null
      ));
      if (!raw.length) return null;
      const visible = zoomDomain
        ? raw.filter(point => {
            const distFt = point.dist_m * M_TO_FT;
            return distFt >= zoomDomain[0] && distFt <= zoomDomain[1];
          })
        : raw;
      // Compatibility must not flip while the user zooms into a flat or narrow
      // section. Keep a bounded fingerprint from the full lap while rendering
      // only the visible points below.
      const compatibilityValues = downsample(raw, 1000)
        .map(point => valueTransform ? valueTransform(point.channel_value) : point.channel_value)
        .filter(Number.isFinite);
      const points = downsample(visible.length ? visible : raw, 5000).map(point => ({
        dist_ft: Math.round(point.dist_m * M_TO_FT),
        value: valueTransform ? valueTransform(point.channel_value) : point.channel_value,
        lat: point.lat,
        lon: point.lon,
        lap_time: point.time ?? null,
        csv_time: (trace.start_time ?? 0) + (point.time ?? 0),
      }));
      const maxPt = points.reduce((best, point) => (
        point.value != null && (best == null || point.value > best.value) ? point : best
      ), null);
      const minPt = points.reduce((best, point) => (
        point.value != null && (best == null || point.value < best.value) ? point : best
      ), null);
      return {
        signalKey: signal.key,
        signalName: signal.name,
        yLabel,
        nativeUnit,
        lapIndex,
        lapNumber: trace.lap_number ?? lapIndex,
        source,
        seriesKey: `${signal.key}:${prefix}${lapIndex}`,
        lapLabel: trace.lap_time_str || `L${trace.lap_number}`,
        color: customColors?.[lapIndex] || palette[colorIndex % palette.length],
        points,
        compatibilityValues,
        maxPt,
        minPt,
      };
    }).filter(Boolean)
  );

  const primary = build(
    signal.lapTraces, signal.selectedLaps, LAP_COLORS, 's1-', customColors1,
    signal.valueTransform, signal.yLabel, signal.unit, 'primary',
  );
  const comparisons = (signal.compareDrivers || []).flatMap(driver => build(
    driver.lapTraces, driver.selectedLaps, driver.palette,
    `s2-${driver.slot}-`, driver.customColors,
    driver.valueTransform !== undefined ? driver.valueTransform : signal.valueTransform,
    driver.yLabel ?? signal.yLabel,
    driver.unit ?? signal.unit,
    driver.slot,
  ));
  return [...primary, ...comparisons].map((item, signalSeriesIndex) => ({
    ...item,
    signalSeriesIndex,
  }));
}

export function ChannelPicker({ availableChannels, selectedChannels, onToggle }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = event => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setFilter('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const optionGroups = partitionPanelChannelOptions(availableChannels, selectedChannels, filter);
  const count = selectedChannels.length;

  const renderChannel = channel => {
    const checked = selectedChannels.some(item => item.key === channel.key);
    const disabled = !checked && count >= MAX_EXTRA_CHANNELS;
    return (
      <label
        key={channel.key}
        className={`${pickerStyles.chItem} ${disabled ? pickerStyles.chItemDisabled : ''}`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={() => onToggle(channel)}
        />
        <span className={pickerStyles.chItemName}>{channel.name}</span>
        {channel.unit && <span className={pickerStyles.chItemUnit}>{channel.unit}</span>}
        {channel.source !== 'primary' && (
          <span className={pickerStyles.chDriverTag}>{channel.driverLabel}</span>
        )}
      </label>
    );
  };

  return (
    <div className={pickerStyles.chPicker} ref={ref}>
      <button
        type="button"
        className={`${styles.optBtn} ${count ? styles.optActive : ''}`}
        onClick={() => setOpen(value => !value)}
        title="Add channels to this plot"
      >Channels{count ? ` (${count})` : ''} ▾</button>
      {open && (
        <div className={pickerStyles.chDropdown}>
          <div className={pickerStyles.chDropdownHdr}>Select up to {MAX_PANEL_CHANNELS} channels total</div>
          <div className={pickerStyles.chFilterWrap}>
            <input
              ref={inputRef}
              className={pickerStyles.chFilter}
              type="text"
              placeholder="Filter channels…"
              value={filter}
              onChange={event => setFilter(event.target.value)}
              onKeyDown={event => event.key === 'Escape' && (filter ? setFilter('') : setOpen(false))}
            />
          </div>
          <div className={pickerStyles.chList}>
            {!optionGroups.selected.length && !optionGroups.available.length && (
              <div className={pickerStyles.chNoMatch}>No channels match</div>
            )}
            {optionGroups.selected.length > 0 && (
              <>
                <div className={pickerStyles.chGroupLabel}>Selected</div>
                {optionGroups.selected.map(renderChannel)}
              </>
            )}
            {optionGroups.available.length > 0 && (
              <>
                <div className={pickerStyles.chGroupLabel}>Available</div>
                {optionGroups.available.map(renderChannel)}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TraceTooltip({ data, anchoredTooltip, unitPrefs, wrapWidth }) {
  if (!data?.entries?.length) return null;
  const scale = TOOLTIP_FONT_SCALES[unitPrefs.tooltipFontScale] ?? 1;
  const tooltipWidth = Math.round(280 * scale);
  let left;
  let right;
  if (anchoredTooltip) {
    left = data.side === 'left' ? 50 : undefined;
    right = data.side === 'right' ? 10 : undefined;
  } else if (data.x > (wrapWidth || 600) * 0.62) {
    right = Math.max(8, (wrapWidth || 600) - data.x + 8);
  } else {
    left = data.x + 14;
  }

  const firstBySignal = new Map();
  const lapTimestamp = formatLapTimestamp(data.lapTime);
  return (
    <div
      className={styles.tooltip}
      style={{
        position: 'absolute', top: 10, left, right, pointerEvents: 'none', zIndex: 20,
        maxWidth: tooltipWidth, fontSize: `${(0.78 * scale).toFixed(3)}rem`,
      }}
    >
      <div
        className={styles.tooltipDist}
        title={lapTimestamp ? 'Elapsed lap time for the first listed trace' : undefined}
      >
        {(convertDistanceFt(data.distFt, unitPrefs) / 1000).toFixed(2)}k {getDistanceUnit(unitPrefs)}
        {lapTimestamp && <> · Lap {lapTimestamp}</>}
      </div>
      {data.entries.map(entry => {
        const reference = firstBySignal.get(entry.signalKey);
        if (!firstBySignal.has(entry.signalKey)) firstBySignal.set(entry.signalKey, entry.value);
        const delta = reference != null && entry.value != null ? entry.value - reference : null;
        return (
          <div key={entry.seriesKey} className={styles.tooltipRow} style={{ borderLeftColor: entry.color }}>
            <span className={styles.tooltipLabel}>{entry.signalName} · {entry.lapLabel}</span>
            <span className={styles.tooltipVal}>
              {fmtValue(entry.value)}
              {entry.yLabel && <span className={styles.tooltipUnit}> {entry.yLabel}</span>}
            </span>
            {delta != null && (
              <span style={{ color: delta > 0 ? '#44cc88' : delta < 0 ? '#e8411a' : '#777', fontSize: '0.897em' }}>
                {delta > 0 ? '+' : ''}{fmtValue(delta)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function MultiChannelTrace({
  lapTraces,
  selectedLaps,
  compareDrivers = [],
  customColors1 = null,
  title,
  signalName = title,
  unit = '',
  units = [],
  yLabel,
  valueTransform,
  additionalSignals = [],
  availableChannels = [],
  selectedAdditionalChannels = [],
  onAdditionalChannelToggle,
  splitMode = false,
  onSplitModeChange,
  sharedScale = false,
  onSharedScaleChange,
  colorMode = 'auto',
  onColorModeChange,
  sectors = null,
  onSectorsChange = null,
  anchoredTooltip = false,
  unitPrefs = {},
  trackName = '',
  sessionDate = '',
  sessionVehicle = '',
  driverName1 = 'Driver 1',
  exportCompareDrivers = [],
  // Panel chrome the caller owns (the Power panel's peak readout, kW toggle and
  // Vehicle button) and the help entry it should open.
  headerExtra = null,
  helpPanel = 'channeltrace',
  // Draw a y=0 rule on the base signal when its range straddles zero (Power:
  // positive drive vs braking), and remap that signal's axis onto the same
  // draggable asymmetric zero-split scale SingleChannelTrace uses, so the
  // handle survives adding channels. Never applied to the additional signals,
  // and suspended while "Same scale" puts every signal on one axis.
  zeroLine = false,
  zeroFracKey = 'channel.zeroFrac',
  exportSlug = 'channeltrace',
  height,
}) {
  const { chartColors } = useTheme();
  const cornerHl = useCornerHighlight();
  const [showMinMax, setShowMinMax] = useState(true);
  const [zoomDomain, setZoomDomain] = useState(null);
  const [naturalExtent, setNaturalExtent] = useState(null);
  // Content-box height and the top padding above it. `.chartWrap` pads the
  // charts (4px top / 2px bottom), so every wrapper-relative y — bands for the
  // hover dots, the zero handle — must start below that padding, and the split
  // sub-charts must divide the content height or the last one overflows.
  const [wrapHeight, setWrapHeight] = useState(0);
  const [wrapPadTop, setWrapPadTop] = useState(0);
  const [wrapWidth, setWrapWidth] = useState(0);
  const [tooltipData, setTooltipData] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionViz, setSelectionViz] = useState(null);
  const [isAnnotateMode, setIsAnnotateMode] = useState(false);
  const [showSectorEditor, setShowSectorEditor] = useState(false);
  const [editingSectorId, setEditingSectorId] = useState(null);
  const [zeroFracRaw, setZeroFracRaw] = useState(() => {
    try { const stored = localStorage.getItem(zeroFracKey); return stored != null ? parseFloat(stored) : null; }
    catch { return null; }
  });
  const draggingZeroRef = useRef(false);
  const zeroFracLiveRef = useRef(null);

  const chartWrapRef = useRef(null);
  const hoverLineRef = useRef(null);
  const signalsRef = useRef([]);
  const seriesRef = useRef([]);
  const zoomDomainRef = useRef(null);
  const naturalExtentRef = useRef(null);
  const isSelectingRef = useRef(false);
  const isAnnotateModeRef = useRef(false);
  const isDirectHoverRef = useRef(false);
  const tooltipSideRef = useRef('right');
  const refAreaLeft = useRef(null);
  const refAreaRight = useRef(null);
  const pendingDragRef = useRef(null);
  const finalizeZoomRef = useRef(null);
  const draggingDividerRef = useRef(null);

  const signalInputs = useMemo(() => [{
    key: `base:${title}`,
    name: signalName,
    unit,
    units,
    yLabel,
    lapTraces,
    selectedLaps,
    compareDrivers,
    valueTransform,
  }, ...additionalSignals], [
    title, signalName, unit, units, yLabel, lapTraces, selectedLaps,
    compareDrivers, valueTransform, additionalSignals,
  ]);

  const independentSignals = useMemo(() => signalInputs.map((signal, index) => {
    const series = buildSeries(signal, customColors1, zoomDomain);
    const values = series.flatMap(item => item.points.map(point => point.value).filter(Number.isFinite));
    return {
      ...signal,
      index,
      series,
      domain: domainForSeries(series),
      isBoolean: values.length > 0 && values.every(value => value === 0 || value === 1),
    };
  }), [signalInputs, customColors1, zoomDomain]);

  const effectiveColorMode = effectivePanelColorMode(colorMode, signalInputs);

  const scaleAssessment = useMemo(() => assessSharedScale(
    independentSignals.map(signal => ({
      name: signal.name,
      units: signal.units,
      values: signal.series.flatMap(item => item.compatibilityValues || []),
    })),
    unitPrefs,
  ), [independentSignals, unitPrefs]);
  const sharedScaleActive = sharedScale && scaleAssessment.compatible;
  const sharedDomain = useMemo(
    () => domainForSeries(independentSignals.flatMap(signal => signal.series)),
    [independentSignals],
  );
  const signals = useMemo(() => independentSignals.map(signal => {
    const scaled = sharedScaleActive ? { ...signal, domain: sharedDomain } : signal;
    if (effectiveColorMode !== 'channel') return scaled;
    const channelColor = CHANNEL_COLORS[signal.index % CHANNEL_COLORS.length];
    return {
      ...scaled,
      series: scaled.series.map(item => ({ ...item, color: channelColor })),
    };
  }), [independentSignals, sharedScaleActive, sharedDomain, effectiveColorMode]);

  const allSeries = useMemo(() => signals.flatMap(signal => signal.series), [signals]);

  // ── Asymmetric zero-split scaling of the base signal ─────────────────────
  // Mirrors SingleChannelTrace: the base axis becomes a normalised [0,1] space
  // with zero at `zeroFrac` (fraction from the bottom) and each half scaled
  // independently to fill its band, so dragging the handle trades pixels
  // between positive and braking power. `signals`/`allSeries` keep real values
  // for hover, tooltip and export; only `renderSignals` carries plotted ones.
  const [baseMin, baseMax] = signals[0]?.domain || [0, 0];
  const naturalZeroFrac = baseMin < 0 && baseMax > 0 ? -baseMin / (baseMax - baseMin) : null;
  const zeroSplitActive = zeroLine && !sharedScaleActive && naturalZeroFrac != null;
  const zeroFrac = zeroSplitActive
    ? clamp(zeroFracRaw ?? naturalZeroFrac, ZERO_FRAC_MIN, ZERO_FRAC_MAX)
    : null;
  const zeroToPlot = value => {
    if (value == null) return null;
    if (value >= 0) return baseMax > 0 ? zeroFrac + (value / baseMax) * (1 - zeroFrac) : zeroFrac;
    return baseMin < 0 ? zeroFrac + (value / -baseMin) * zeroFrac : zeroFrac;
  };
  const zeroToReal = plotted => (plotted >= zeroFrac
    ? (plotted - zeroFrac) / (1 - zeroFrac) * baseMax
    : (plotted - zeroFrac) / zeroFrac * -baseMin);
  const renderSignals = useMemo(() => {
    if (!zeroSplitActive) return signals;
    const [base, ...rest] = signals;
    const plotPt = point => (point ? { ...point, value: zeroToPlot(point.value), realValue: point.value } : point);
    return [{
      ...base,
      domain: [0, 1],
      zeroSplit: {
        ticks: niceTicks(baseMin, baseMax).map(zeroToPlot),
        tickFormatter: plotted => fmtAxis(zeroToReal(plotted)),
        toFrac: zeroToPlot,
        zeroY: zeroFrac,
      },
      series: base.series.map(item => ({
        ...item,
        points: item.points.map(plotPt),
        maxPt: plotPt(item.maxPt),
        minPt: plotPt(item.minPt),
      })),
    }, ...rest];
    // zeroToPlot/zeroToReal close over zeroFrac, baseMin and baseMax.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals, zeroSplitActive, zeroFrac, baseMin, baseMax]);
  const xDomain = useMemo(() => {
    if (zoomDomain) return zoomDomain;
    if (naturalExtent) return naturalExtent;
    const distances = allSeries.flatMap(item => item.points.map(point => point.dist_ft));
    return arrayExtent(distances);
  }, [zoomDomain, naturalExtent, allSeries]);

  // Sector fractions are measured against the full raw lap, never the zoomed
  // or downsampled render series, so editing remains stable at every zoom level.
  const lapTotalFt = useMemo(() => {
    let maxFt = 0;
    selectedLaps.forEach(lapIndex => {
      const trace = lapTraces?.[lapIndex];
      (trace?.points || []).forEach(point => {
        if (point.dist_m != null) maxFt = Math.max(maxFt, point.dist_m * M_TO_FT);
      });
    });
    return maxFt || null;
  }, [lapTraces, selectedLaps]);

  useEffect(() => {
    signalsRef.current = signals;
    seriesRef.current = allSeries;
  }, [signals, allSeries]);

  useEffect(() => subscribeExtent(extent => {
    naturalExtentRef.current = extent;
    setNaturalExtent(extent);
  }), []);

  useEffect(() => subscribeZoom(domain => {
    zoomDomainRef.current = domain;
    setZoomDomain(domain);
  }), []);

  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return undefined;
    const measure = () => {
      const style = getComputedStyle(wrap);
      const padTop = parseFloat(style.paddingTop) || 0;
      const padBottom = parseFloat(style.paddingBottom) || 0;
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      setWrapPadTop(padTop);
      setWrapHeight(Math.max(0, wrap.clientHeight - padTop - padBottom));
      setWrapWidth(Math.max(0, wrap.clientWidth - padLeft - padRight));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    measure();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handler = event => {
      const active = !!event.detail?.active;
      setIsAnnotateMode(active);
      isAnnotateModeRef.current = active;
    };
    window.addEventListener('trackapp:annotate-mode', handler);
    return () => window.removeEventListener('trackapp:annotate-mode', handler);
  }, []);

  const splitLayout = useMemo(() => {
    if (!splitMode || signals.length < 2 || !wrapHeight) return null;
    const usable = wrapHeight - (signals.length - 1) * SPLIT_DIVIDER_H;
    const subHeight = Math.floor(usable / signals.length);
    if (subHeight < MIN_SUB_H) return null;
    const bands = {};
    signals.forEach((signal, index) => {
      bands[signal.key] = {
        top: wrapPadTop + index * (subHeight + SPLIT_DIVIDER_H) + CHART_TOP,
        plotH: subHeight - CHART_TOP - CHART_BOTTOM,
      };
    });
    return { subHeight, bands };
  }, [splitMode, signals, wrapHeight, wrapPadTop]);
  const splitActive = !!splitLayout;
  const extraAxisCount = sharedScaleActive ? 0 : additionalSignals.length;
  // Preserve enough room for every independent right-side label. This was
  // previously hard-coded for one or two extras and clipped higher counts.
  const rightMargin = splitActive
    ? RIGHT_MARGIN
    : RIGHT_MARGIN + extraAxisCount * 32;
  // Recharts removes both margin.right and every right-hand YAxis width from
  // the plot area. Pointer math must account for the same total or the shared
  // cursor drifts farther off-scale with each added signal.
  const rightPad = rightMargin + (splitActive ? 0 : extraAxisCount * 34);
  const rightPadRef = useRef(rightPad);
  rightPadRef.current = rightPad;

  const pixelToDistFt = clientX => {
    const wrap = chartWrapRef.current;
    const allDists = seriesRef.current.flatMap(item => item.points.map(point => point.dist_ft));
    if (!wrap || !allDists.length) return null;
    const domain = zoomDomainRef.current || naturalExtentRef.current || arrayExtent(allDists) || [0, 0];
    if (domain[0] === domain[1]) return null;
    const rect = wrap.getBoundingClientRect();
    const chartWidth = rect.width - Y_AXIS_W - rightPadRef.current;
    if (chartWidth <= 0) return null;
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left - Y_AXIS_W) / chartWidth));
    return domain[0] + fraction * (domain[1] - domain[0]);
  };
  const pixelToDistFtRef = useRef(pixelToDistFt);
  pixelToDistFtRef.current = pixelToDistFt;

  const sectorBoundariesFt = useMemo(() => (
    sectors && lapTotalFt
      ? sectors.slice(0, -1).map(sector => sector.end_frac * lapTotalFt)
      : []
  ), [sectors, lapTotalFt]);

  const getMinSectorGapFrac = () => {
    const wrap = chartWrapRef.current;
    if (!wrap || !xDomain || !lapTotalFt) return 0.005;
    const chartWidth = wrap.clientWidth - Y_AXIS_W - rightPadRef.current;
    if (chartWidth <= 0) return 0.005;
    const feetPerPixel = (xDomain[1] - xDomain[0]) / chartWidth;
    return Math.max(0.005, (60 * feetPerPixel) / lapTotalFt);
  };

  const applySectorBoundaryFracs = fracs => {
    if (!onSectorsChange) return;
    const sorted = [...fracs].filter(frac => frac > 0 && frac < 1).sort((a, b) => a - b);
    const cuts = [0, ...sorted, 1];
    onSectorsChange(cuts.slice(0, -1).map((start, index) => {
      const existing = sectors?.[index];
      return {
        id: existing?.id || `S${index + 1}`,
        label: existing?.label || `Sector ${index + 1}`,
        start_frac: start,
        end_frac: cuts[index + 1],
      };
    }));
  };

  useEffect(() => {
    if (!showSectorEditor) return undefined;
    const handleMove = event => {
      if (draggingDividerRef.current == null || !lapTotalFt) return;
      const distFt = pixelToDistFtRef.current(event.clientX);
      if (distFt == null) return;
      const index = draggingDividerRef.current;
      const fracs = (sectors || []).slice(0, -1).map(sector => sector.end_frac);
      const gap = getMinSectorGapFrac();
      const low = index > 0 ? fracs[index - 1] + gap : gap;
      const high = index < fracs.length - 1 ? fracs[index + 1] - gap : 1 - gap;
      fracs[index] = Math.max(low, Math.min(high, distFt / lapTotalFt));
      applySectorBoundaryFracs(fracs);
    };
    const handleUp = () => { draggingDividerRef.current = null; };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    // The handlers deliberately close over the latest controlled sector list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSectorEditor, sectors, lapTotalFt, xDomain, rightPad]);

  const tooltipAt = (distFt, xOverride = null) => {
    const wrap = chartWrapRef.current;
    const allDists = seriesRef.current.flatMap(item => item.points.map(point => point.dist_ft));
    if (!wrap || !allDists.length) return null;
    const domain = zoomDomainRef.current || naturalExtentRef.current || arrayExtent(allDists) || [0, 0];
    if (domain[0] === domain[1]) return null;
    const chartWidth = wrap.clientWidth - Y_AXIS_W - rightPadRef.current;
    const fraction = Math.max(0, Math.min(1, (distFt - domain[0]) / (domain[1] - domain[0])));
    const x = xOverride ?? Y_AXIS_W + fraction * chartWidth;
    const entries = seriesRef.current.map(item => {
      const point = findNearest(item.points, distFt);
      return point ? {
        seriesKey: item.seriesKey,
        signalKey: item.signalKey,
        signalName: item.signalName,
        yLabel: item.yLabel,
        lapLabel: item.lapLabel,
        color: item.color,
        value: valueAt(item.points, distFt),
        lapTime: point.lap_time,
      } : null;
    }).filter(Boolean);
    if (!entries.length) return null;
    const position = x / (wrap.clientWidth || 600);
    if (position < 0.45) tooltipSideRef.current = 'right';
    else if (position > 0.55) tooltipSideRef.current = 'left';
    const lapTime = entries.find(entry => Number.isFinite(entry.lapTime))?.lapTime ?? null;
    return { x, distFt, lapTime, entries, side: tooltipSideRef.current };
  };

  const publishCursor = distFt => {
    const point = findNearest(seriesRef.current[0]?.points || [], distFt);
    if (point?.lat && point?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: { lat: point.lat, lon: point.lon, distFt, videoTime: point.csv_time ?? null },
      }));
    }
  };

  const moveCrosshair = (distFt, xOverride = null) => {
    const tooltip = tooltipAt(distFt, xOverride);
    if (!tooltip) return;
    if (hoverLineRef.current) {
      hoverLineRef.current.style.left = `${tooltip.x}px`;
      hoverLineRef.current.style.display = 'block';
    }
    setTooltipData(tooltip);
  };

  useEffect(() => {
    const handleExternalCursor = event => {
      if (isDirectHoverRef.current) return;
      const distFt = event.detail?.distFt;
      if (distFt == null) {
        setTooltipData(null);
        if (hoverLineRef.current) hoverLineRef.current.style.display = 'none';
        return;
      }
      const zoom = zoomDomainRef.current;
      if (zoom && (distFt < zoom[0] || distFt > zoom[1])) return;
      moveCrosshair(distFt);
    };
    window.addEventListener('trackapp:map-hover', handleExternalCursor);
    window.addEventListener('trackapp:chart-hover', handleExternalCursor);
    const last = lastCursor();
    if (last) handleExternalCursor({ detail: last });
    return () => {
      window.removeEventListener('trackapp:map-hover', handleExternalCursor);
      window.removeEventListener('trackapp:chart-hover', handleExternalCursor);
    };
  }, []);

  const resetZoom = () => {
    publishZoom(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', { detail: null }));
  };

  const dispatchZoom = domain => {
    publishZoom(domain);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: domain[0], distFtMax: domain[1] },
    }));
  };

  // A press that never armed a drag is a plain click. Recharts' own onClick
  // cannot carry this: the chart re-renders between mousedown and mouseup
  // (any parent state change does it), React replaces the <path> the press
  // landed on, and with the mousedown target detached the browser never fires
  // a click event at all — not on the chart, not on document. mouseup always
  // arrives, so the click is synthesised from it.
  const emitCursorClick = distFt => {
    const point = findNearest(seriesRef.current[0]?.points || [], distFt);
    if (point?.lat && point?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-click', {
        detail: { lat: point.lat, lon: point.lon, distFt, videoTime: point.csv_time ?? null },
      }));
    }
  };

  const finalizeZoom = () => {
    const pending = pendingDragRef.current;
    pendingDragRef.current = null;
    if (!isSelectingRef.current) {
      if (pending) emitCursorClick(pending.distFt);
      return;
    }
    isSelectingRef.current = false;
    setIsSelecting(false);
    const left = refAreaLeft.current;
    const right = refAreaRight.current;
    refAreaLeft.current = null;
    refAreaRight.current = null;
    setSelectionViz(null);
    if (left == null || right == null || left === right) return;
    const domain = left < right ? [left, right] : [right, left];
    if (isAnnotateModeRef.current) {
      window.dispatchEvent(new CustomEvent('trackapp:annotation-captured', {
        detail: { channel: title, channelLabel: title, distFtStart: domain[0], distFtEnd: domain[1] },
      }));
      setIsAnnotateMode(false);
      isAnnotateModeRef.current = false;
      window.dispatchEvent(new CustomEvent('trackapp:annotate-mode', { detail: { active: false } }));
    }
    dispatchZoom(domain);
  };
  finalizeZoomRef.current = finalizeZoom;

  useEffect(() => {
    const handleMouseUp = () => finalizeZoomRef.current?.();
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Record the press but do not touch state yet — a plain click must leave the
  // chart untouched between mousedown and mouseup, otherwise the re-render
  // (the zoom ReferenceArea appearing) removes the element the press landed on
  // and the browser never delivers a click for Recharts to forward.
  const handleMouseDown = (state, event) => {
    const clientX = event?.clientX;
    const distFt = clientX != null
      ? pixelToDistFtRef.current(clientX)
      : (state?.activeLabel != null ? Number(state.activeLabel) : null);
    if (distFt == null) return;
    pendingDragRef.current = { distFt, clientX: clientX ?? null };
  };

  const handleMouseMove = (state, event) => {
    isDirectHoverRef.current = true;

    // Promote a pending press into a zoom drag once it has actually moved.
    if (pendingDragRef.current && !isSelectingRef.current) {
      const start = pendingDragRef.current;
      const moved = start.clientX != null && event?.clientX != null
        ? Math.abs(event.clientX - start.clientX)
        : DRAG_ARM_PX; // no clientX available — arm immediately (safe default)
      if (moved < DRAG_ARM_PX) return;
      isSelectingRef.current = true;
      setIsSelecting(true);
      refAreaLeft.current = start.distFt;
      refAreaRight.current = start.distFt;
      setSelectionViz({ left: start.distFt, right: start.distFt });
    }

    const distFt = state?.activeLabel != null ? Number(state.activeLabel) : null;
    if (distFt == null) return;
    if (isSelectingRef.current) {
      refAreaRight.current = distFt;
      setSelectionViz({ left: refAreaLeft.current, right: distFt });
      return;
    }
    moveCrosshair(distFt, state.activeCoordinate?.x);
    publishCursor(distFt);
  };

  const handleMouseLeave = () => {
    isDirectHoverRef.current = false;
    pendingDragRef.current = null;
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      setIsSelecting(false);
      setSelectionViz(null);
    }
    setTooltipData(null);
    if (hoverLineRef.current) hoverLineRef.current.style.display = 'none';
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  };

  const onPanFt = deltaFt => {
    const zoom = zoomDomainRef.current;
    if (!zoom) return;
    const natural = naturalExtentRef.current || zoom;
    const size = zoom[1] - zoom[0];
    let low = zoom[0] + deltaFt;
    let high = zoom[1] + deltaFt;
    if (low < natural[0]) { low = natural[0]; high = low + size; }
    if (high > natural[1]) { high = natural[1]; low = high - size; }
    dispatchZoom([low, high]);
  };
  const onPanFtRef = useRef(onPanFt);
  onPanFtRef.current = onPanFt;

  const onPinchZoom = (scaleFactor, centerFt) => {
    const natural = naturalExtentRef.current;
    if (!natural) return;
    const current = zoomDomainRef.current || natural;
    const center = Math.max(current[0], Math.min(current[1], centerFt ?? (current[0] + current[1]) / 2));
    const low = Math.max(natural[0], center - (center - current[0]) / scaleFactor);
    const high = Math.min(natural[1], center + (current[1] - center) / scaleFactor);
    if (high - low >= 200) dispatchZoom([low, high]);
  };
  const onPinchZoomRef = useRef(onPinchZoom);
  onPinchZoomRef.current = onPinchZoom;

  const onCrosshairMove = clientX => {
    const distFt = pixelToDistFtRef.current(clientX);
    if (distFt == null) return;
    const wrap = chartWrapRef.current;
    const rect = wrap.getBoundingClientRect();
    const x = Y_AXIS_W + Math.max(0, Math.min(
      wrap.clientWidth - Y_AXIS_W - rightPadRef.current,
      clientX - rect.left - Y_AXIS_W,
    ));
    moveCrosshair(distFt, x);
    publishCursor(distFt);
  };
  const onCrosshairMoveRef = useRef(onCrosshairMove);
  onCrosshairMoveRef.current = onCrosshairMove;
  const onCrosshairClearRef = useRef(() => {});
  onCrosshairClearRef.current = handleMouseLeave;

  usePanOnWheel({
    chartWrapRef, zoomDomainRef, leftPad: Y_AXIS_W, rightPadRef, onPanFtRef,
  });
  useTouchChartGestures({
    chartWrapRef,
    pixelToDistFtRef,
    onCrosshairMoveRef,
    onCrosshairClearRef,
    isSelectingRef,
    refAreaLeft,
    refAreaRight,
    setIsSelecting,
    setSelectionViz,
    finalizeZoomRef,
    isDirectHoverRef,
    onPanFtRef,
    onPinchZoomRef,
  });

  const chartEvents = {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: finalizeZoom,
    onMouseLeave: handleMouseLeave,
  };

  const xAxis = visible => (
    <XAxis
      dataKey="dist_ft"
      type="number"
      domain={zoomDomain || naturalExtent || ['auto', 'auto']}
      tickFormatter={value => `${(convertDistanceFt(value, unitPrefs) / 1000).toFixed(1)}k`}
      stroke={visible ? chartColors.axis : 'transparent'}
      tick={{ fill: visible ? chartColors.tick : 'transparent', fontSize: 10 }}
      tickLine={visible}
      axisLine={visible}
      height={18}
      allowDuplicatedCategory={false}
      label={visible ? {
        value: `${getDistanceUnit(unitPrefs)} from lap start`,
        position: 'insideBottomRight', offset: -4, fill: chartColors.label, fontSize: 9,
      } : undefined}
    />
  );

  const yAxis = (signal, split, index) => {
    const axisLabel = split || index > 0
      ? `${signal.name}${signal.yLabel ? ` (${signal.yLabel})` : ''}`
      : signal.yLabel || signal.name;
    return (
      <YAxis
        key={signal.key}
        yAxisId={signal.key}
        orientation={split || index === 0 ? 'left' : 'right'}
        domain={signal.domain}
        ticks={signal.zeroSplit?.ticks}
        stroke={chartColors.axis}
        tick={{ fill: chartColors.tick, fontSize: 10 }}
        tickFormatter={signal.zeroSplit?.tickFormatter || fmtAxis}
        label={{
          value: axisLabel,
          angle: split || index === 0 ? -90 : 90,
          position: split || index === 0 ? 'insideLeft' : 'insideRight',
          offset: split || index === 0 ? 16 : 10 + (index - 1) * 34,
          fill: chartColors.label,
          fontSize: 10,
        }}
        width={split || index === 0 ? Y_AXIS_W : 34}
      />
    );
  };

  const sharedYAxis = (
    <YAxis
      yAxisId="shared"
      orientation="left"
      domain={sharedDomain}
      stroke={chartColors.axis}
      tick={{ fill: chartColors.tick, fontSize: 10 }}
      tickFormatter={fmtAxis}
      label={{
        value: scaleAssessment.displayUnit || 'Shared scale',
        angle: -90,
        position: 'insideLeft',
        offset: 16,
        fill: chartColors.label,
        fontSize: 10,
      }}
      width={Y_AXIS_W}
    />
  );

  const axisIdFor = (signal, split) => (
    !split && sharedScaleActive ? 'shared' : signal.key
  );

  const widthForSignal = (index, split) => (
    split ? 1.5 : [1.5, 2.5, 3.5][index] || 1.5
  );

  const opacityForSignal = (index, split) => (
    effectiveColorMode === 'channel' || split ? 1 : [1, 0.78, 0.55][index] || 1
  );

  const opacityForLine = (signal, item, split) => (
    effectiveColorMode === 'channel'
      ? CHANNEL_LAP_OPACITIES[(item.signalSeriesIndex || 0) % CHANNEL_LAP_OPACITIES.length]
      : opacityForSignal(signal.index, split)
  );

  const linesFor = (signal, split) => signal.series.map(item => (
    <Line
      key={item.seriesKey}
      yAxisId={axisIdFor(signal, split)}
      data={item.points}
      dataKey="value"
      name={item.lapLabel}
      type={signal.isBoolean ? 'stepAfter' : undefined}
      stroke={item.color}
      strokeWidth={widthForSignal(signal.index, split)}
      strokeOpacity={opacityForLine(signal, item, split)}
      dot={false}
      isAnimationActive={false}
      activeDot={false}
      connectNulls={false}
    />
  ));

  const minMaxFor = (signal, split) => showMinMax && signal.series.flatMap(item => [
    item.maxPt && (
      <ReferenceDot
        key={`max-${item.seriesKey}`}
        yAxisId={axisIdFor(signal, split)}
        x={item.maxPt.dist_ft}
        y={item.maxPt.value}
        r={4}
        fill={item.color}
        stroke="#fff"
        strokeWidth={1.2}
        label={{ value: fmtValue(item.maxPt.realValue ?? item.maxPt.value), position: 'bottom', fill: item.color, fontSize: 9 }}
      />
    ),
    item.minPt && (
      <ReferenceDot
        key={`min-${item.seriesKey}`}
        yAxisId={axisIdFor(signal, split)}
        x={item.minPt.dist_ft}
        y={item.minPt.value}
        r={4}
        fill={item.color}
        stroke="#fff"
        strokeWidth={1.2}
        label={{ value: fmtValue(item.minPt.realValue ?? item.minPt.value), position: 'top', fill: item.color, fontSize: 9 }}
      />
    ),
  ].filter(Boolean));

  const zeroLineFor = (signal, split) => {
    if (!zeroLine || signal.index !== 0) return null;
    const [low, high] = (!split && sharedScaleActive) ? sharedDomain : (signals[0]?.domain || [0, 0]);
    if (!(low < 0 && high > 0)) return null;
    return (
      <ReferenceLine
        yAxisId={axisIdFor(signal, split)}
        y={signal.zeroSplit ? signal.zeroSplit.zeroY : 0}
        stroke={chartColors.axis}
        strokeWidth={1.5}
        strokeOpacity={0.85}
      />
    );
  };

  const areasFor = (signal, split) => (
    <>
      {cornerHl?.distFtMin != null && (
        <ReferenceArea
          yAxisId={axisIdFor(signal, split)}
          x1={cornerHl.distFtMin}
          x2={cornerHl.distFtMax}
          fill="#f5c842"
          fillOpacity={0.07}
          stroke="#f5c842"
          strokeOpacity={0.35}
          strokeDasharray="4 3"
        />
      )}
      {selectionViz && selectionViz.left !== selectionViz.right && (
        <ReferenceArea
          yAxisId={axisIdFor(signal, split)}
          x1={Math.min(selectionViz.left, selectionViz.right)}
          x2={Math.max(selectionViz.left, selectionViz.right)}
          fill={isAnnotateMode ? '#4f9cf5' : '#f5c842'}
          fillOpacity={0.15}
          stroke={isAnnotateMode ? '#4f9cf5' : '#f5c842'}
          strokeOpacity={0.6}
        />
      )}
    </>
  );

  const hoverDots = tooltipData?.entries.map(entry => {
    const signal = signals.find(item => item.key === entry.signalKey);
    if (!signal) return null;
    const [low, high] = signal.domain;
    const band = splitActive
      ? splitLayout.bands[signal.key]
      : { top: wrapPadTop + CHART_TOP, plotH: wrapHeight - CHART_TOP - CHART_BOTTOM };
    if (!(band.plotH > 0) || high === low) return null;
    const frac = signal.index === 0 && renderSignals[0]?.zeroSplit
      ? renderSignals[0].zeroSplit.toFrac(entry.value)
      : (entry.value - low) / (high - low);
    if (!Number.isFinite(frac)) return null;
    const y = band.top + (1 - frac) * band.plotH;
    return (
      <div key={`dot-${entry.seriesKey}`} style={{
        position: 'absolute', left: tooltipData.x - 5, top: y - 5,
        width: 10, height: 10, borderRadius: '50%', background: entry.color,
        border: '2px solid #fff', pointerEvents: 'none', zIndex: 15,
      }} />
    );
  });

  // Band the base signal occupies — the whole plot in overlay mode, its own
  // sub-chart in split mode — for placing and dragging the zero handle.
  const baseBand = splitActive
    ? splitLayout.bands[signals[0]?.key]
    : { top: wrapPadTop + CHART_TOP, plotH: wrapHeight - CHART_TOP - CHART_BOTTOM };
  const zeroHandlePx = zeroSplitActive && baseBand?.plotH > 0
    ? baseBand.top + (1 - zeroFrac) * baseBand.plotH
    : null;
  const onZeroHandleMove = event => {
    if (!draggingZeroRef.current || !baseBand || !(baseBand.plotH > 0)) return;
    const rect = chartWrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const frac = clamp(
      1 - (event.clientY - rect.top - baseBand.top) / baseBand.plotH,
      ZERO_FRAC_MIN, ZERO_FRAC_MAX,
    );
    zeroFracLiveRef.current = frac;
    setZeroFracRaw(frac);
  };
  const onZeroHandleDown = event => {
    event.stopPropagation();
    draggingZeroRef.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onZeroHandleUp = event => {
    draggingZeroRef.current = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (zeroFracLiveRef.current != null) {
      try { localStorage.setItem(zeroFracKey, String(zeroFracLiveRef.current)); } catch { /* ignore */ }
    }
  };
  const onZeroHandleReset = event => {
    event.stopPropagation();
    zeroFracLiveRef.current = null;
    setZeroFracRaw(null);
    try { localStorage.removeItem(zeroFracKey); } catch { /* ignore */ }
  };

  const handleExportPng = () => {
    const chartSvgs = [...(chartWrapRef.current?.querySelectorAll('svg.recharts-surface') || [])];
    const svg = chartSvgs[0] || chartWrapRef.current?.querySelector('svg');
    if (!svg || !allSeries.length) return;
    const signalLabels = signals.map(signal => (
      signal.yLabel ? `${signal.name} (${signal.yLabel})` : signal.name
    ));
    const viewBits = [
      `${signalLabels.join(' · ')} vs Distance`,
      splitActive ? 'Split' : null,
      sharedScaleActive ? 'Same scale' : null,
      `Colors by ${effectiveColorMode}`,
    ].filter(Boolean);
    exportTracePanelPng({
      svgs: splitActive && chartSvgs.length > 1 ? chartSvgs : [svg],
      themeFrom: chartWrapRef.current,
      series: allSeries,
      fallbackTitle: title,
      subtitle: viewBits.join(' · '),
      slugBits: [signalName || 'channel', exportSlug],
      plotTop: CHART_TOP,
      plotBottom: CHART_BOTTOM,
      trackName,
      sessionDate,
      sessionVehicle,
      driverName1,
      exportCompareDrivers,
    }).catch(error => console.error('Chart export failed:', error));
  };

  const sharedScaleTitle = scaleAssessment.compatible
    ? scaleAssessment.inferred
      ? 'Use one Y-axis domain for all channels. Compatibility was inferred from names and observed ranges.'
      : 'Use one Y-axis domain for all displayed channels.'
    : scaleAssessment.reason;
  const colorModeTitle = colorMode === 'auto'
    ? `Automatically color by ${effectiveColorMode} for the current selection`
    : `Color traces by ${colorMode}`;

  return (
    <div className={styles.panel} style={height != null ? { height, minHeight: height } : { height: '100%' }}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>{title}</span>}>
        {signals.length > 1 && (
          <div className={styles.signalLegend} aria-label="Channel line styles">
            {signals.map(signal => (
              <span
                key={signal.key}
                className={styles.signalLegendItem}
                title={signal.name}
                style={effectiveColorMode === 'channel'
                  ? { color: CHANNEL_COLORS[signal.index % CHANNEL_COLORS.length] }
                  : undefined}
              >
                <svg width="18" height="6" aria-hidden="true">
                  <line
                    x1="1" y1="3" x2="17" y2="3"
                    stroke="currentColor"
                    strokeWidth={widthForSignal(signal.index, false)}
                    strokeOpacity={opacityForSignal(signal.index, false)}
                  />
                </svg>
                <span>{signal.name}</span>
              </span>
            ))}
          </div>
        )}
        <div className={styles.opts}>
          {headerExtra}
          <ChannelPicker
            availableChannels={availableChannels}
            selectedChannels={selectedAdditionalChannels}
            onToggle={onAdditionalChannelToggle}
          />
          {selectedAdditionalChannels.length > 0 && onColorModeChange && (
            <select
              className={`${styles.optBtn} ${styles.colorModeSelect}`}
              value={colorMode}
              onChange={event => onColorModeChange(event.target.value)}
              title={colorModeTitle}
              aria-label="Color channel traces by"
            >
              <option value="auto">Color: Auto</option>
              <option value="channel">Color: Channel</option>
              <option value="lap">Color: Lap</option>
            </select>
          )}
          <button
            type="button"
            className={styles.optBtn}
            onClick={handleExportPng}
            title={splitActive
              ? 'Download the full split channel chart as a PNG image with a driver / lap-time legend'
              : 'Download this channel chart as a PNG image with a driver / lap-time legend'}
          >PNG ⤓</button>
          {selectedAdditionalChannels.length > 0 && (
            <button
              type="button"
              className={`${styles.optBtn} ${splitMode ? styles.optActive : ''}`}
              onClick={onSplitModeChange}
              title="Give each selected channel its own chart"
            >Split</button>
          )}
          {selectedAdditionalChannels.length > 0 && onSharedScaleChange && (
            <button
              type="button"
              className={`${styles.optBtn} ${sharedScaleActive ? styles.optActive : ''}`}
              onClick={onSharedScaleChange}
              disabled={!scaleAssessment.compatible}
              title={sharedScaleTitle}
            >Same scale</button>
          )}
          <button
            type="button"
            className={`${styles.optBtn} ${showMinMax ? styles.optActive : ''}`}
            onClick={() => setShowMinMax(value => !value)}
          >Min/Max</button>
          {onSectorsChange && (
            <button
              type="button"
              className={`${styles.optBtn} ${showSectorEditor ? styles.optActive : ''}`}
              onClick={() => {
                setShowSectorEditor(value => !value);
                setEditingSectorId(null);
              }}
              title="Edit sectors — drag to move, click × to remove, click label to rename, click between dividers to add"
              style={showSectorEditor ? { color: '#FFD700' } : {}}
            >Sectors</button>
          )}
          <HelpButton panel={helpPanel} page="sessions" className={styles.optBtn} />
        </div>
      </PanelHeader>

      <div
        className={styles.chartWrap}
        ref={chartWrapRef}
        onDoubleClick={resetZoom}
        style={{
          position: 'relative',
          cursor: isSelecting ? (isAnnotateMode ? 'crosshair' : 'ew-resize') : (isAnnotateMode ? 'crosshair' : 'col-resize'),
          userSelect: 'none',
          outline: isAnnotateMode ? '2px solid #4f9cf5' : 'none',
          outlineOffset: '-2px',
        }}
      >
        {showSectorEditor && lapTotalFt && xDomain && xDomain[1] !== xDomain[0] && (() => {
          const wrap = chartWrapRef.current;
          if (!wrap) return null;
          const chartWidth = wrap.clientWidth - Y_AXIS_W - rightPadRef.current;
          if (chartWidth <= 0) return null;
          const xToPx = distFt => (
            Y_AXIS_W + chartWidth * (distFt - xDomain[0]) / (xDomain[1] - xDomain[0])
          );
          const handleAddClick = event => {
            const distFt = pixelToDistFtRef.current(event.clientX);
            if (distFt == null) return;
            const newFrac = Math.max(0.01, Math.min(0.99, distFt / lapTotalFt));
            const existing = (sectors || []).slice(0, -1).map(sector => sector.end_frac);
            const gap = getMinSectorGapFrac();
            if ([0, ...existing, 1].some(frac => Math.abs(frac - newFrac) < gap)) return;
            applySectorBoundaryFracs([...existing, newFrac]);
          };
          return (
            <div
              onClick={handleAddClick}
              style={{
                position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
                zIndex: 8, pointerEvents: 'auto', cursor: 'crosshair',
                background: 'rgba(255,215,0,0.04)',
              }}
            >
              {sectorBoundariesFt.map((distFt, index) => {
                const x = xToPx(distFt);
                if (x < Y_AXIS_W - 4 || x > Y_AXIS_W + chartWidth + 4) return null;
                return (
                  <div
                    key={sectors?.[index]?.id || index}
                    onMouseDown={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      draggingDividerRef.current = index;
                    }}
                    onClick={event => event.stopPropagation()}
                    title={`Sector boundary ${index + 1} — drag to move`}
                    style={{
                      position: 'absolute', left: x - 4, top: 4, bottom: 28,
                      width: 8, zIndex: 10, cursor: 'ew-resize', background: 'transparent',
                    }}
                  >
                    <div style={{
                      position: 'absolute', left: 3, top: 0, bottom: 0, width: 2,
                      background: '#FFD700', opacity: 0.9, pointerEvents: 'none',
                    }} />
                    <div
                      onClick={event => {
                        event.stopPropagation();
                        const fracs = (sectors || []).slice(0, -1).map(sector => sector.end_frac);
                        fracs.splice(index, 1);
                        applySectorBoundaryFracs(fracs);
                      }}
                      onMouseDown={event => event.stopPropagation()}
                      title="Remove this sector boundary"
                      style={{
                        position: 'absolute', left: -4, top: 0, width: 16, height: 16,
                        borderRadius: '50%', background: '#FFD700', border: '1px solid #1a1300',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 900, color: '#1a1300', pointerEvents: 'auto',
                        userSelect: 'none', lineHeight: 1,
                      }}
                    >×</div>
                  </div>
                );
              })}
              {(sectors || []).map(sector => {
                const centerFt = (sector.start_frac + sector.end_frac) / 2 * lapTotalFt;
                const x = xToPx(centerFt);
                if (x < Y_AXIS_W - 20 || x > Y_AXIS_W + chartWidth + 20) return null;
                if (editingSectorId === sector.id) {
                  return (
                    <input
                      key={sector.id}
                      autoFocus
                      defaultValue={sector.label}
                      onBlur={event => {
                        const label = event.target.value.trim() || sector.label;
                        onSectorsChange((sectors || []).map(item => (
                          item.id === sector.id ? { ...item, label } : item
                        )));
                        setEditingSectorId(null);
                      }}
                      onKeyDown={event => {
                        if (event.key === 'Enter') event.target.blur();
                        if (event.key === 'Escape') setEditingSectorId(null);
                      }}
                      onClick={event => event.stopPropagation()}
                      onMouseDown={event => event.stopPropagation()}
                      style={{
                        position: 'absolute', left: x - 40, top: 3, width: 80,
                        fontSize: 9, color: '#FFD700', background: '#1a1a00',
                        border: '1px solid #FFD700', borderRadius: 3, textAlign: 'center',
                        padding: '1px 3px', fontWeight: 600, fontFamily: 'inherit', zIndex: 20,
                      }}
                    />
                  );
                }
                return (
                  <div
                    key={sector.id}
                    onClick={event => {
                      event.stopPropagation();
                      setEditingSectorId(sector.id);
                    }}
                    title={`${sector.label} — click to rename`}
                    style={{
                      position: 'absolute', left: x - 24, top: 6, width: 48,
                      textAlign: 'center', fontSize: 9, color: '#FFD700', opacity: 0.8,
                      pointerEvents: 'auto', fontWeight: 600, zIndex: 1, cursor: 'text',
                    }}
                  >{sector.label.replace('Sector ', 'S')}</div>
                );
              })}
            </div>
          );
        })()}

        <div ref={hoverLineRef} style={{
          display: 'none', position: 'absolute', top: 8, bottom: 4, width: 1,
          background: '#f5c842', opacity: 0.85, pointerEvents: 'none', zIndex: 10,
        }} />
        {hoverDots}

        {/* Draggable zero-line handle on the base signal's Y-axis */}
        {zeroHandlePx != null && (
          <div
            onPointerDown={onZeroHandleDown}
            onPointerMove={onZeroHandleMove}
            onPointerUp={onZeroHandleUp}
            onMouseDown={event => event.stopPropagation()}
            onDoubleClick={onZeroHandleReset}
            title="Drag to move the zero line — trades panel height between positive and braking power (double-click to auto-fit)"
            style={{
              position: 'absolute', left: 0, top: zeroHandlePx - 9,
              width: Y_AXIS_W + 13, height: 18,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              cursor: 'ns-resize', zIndex: 16, touchAction: 'none',
            }}
          >
            <div style={{
              width: 16, height: 14, borderRadius: 3,
              background: chartColors.axis,
              border: '1px solid rgba(255,255,255,0.25)',
              boxShadow: '0 0 3px rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#0b0e1a', fontSize: 10, lineHeight: 1, fontWeight: 700,
            }}>⇕</div>
          </div>
        )}
        <TraceTooltip
          data={tooltipData}
          anchoredTooltip={anchoredTooltip}
          unitPrefs={unitPrefs}
          wrapWidth={wrapWidth}
        />

        {!signals[0]?.series.length ? (
          <div className={styles.empty}>No {title.toLowerCase()} data</div>
        ) : splitActive ? (
          wrapWidth > 0 && renderSignals.map((signal, index) => (
            <div key={signal.key}>
              {index > 0 && <div style={{ height: SPLIT_DIVIDER_H, background: chartColors.grid }} />}
              <div style={{ height: splitLayout.subHeight, position: 'relative', overflow: 'hidden' }}>
                <LineChart
                  syncId="track"
                  syncMethod="value"
                  accessibilityLayer={false}
                  width={wrapWidth}
                  height={splitLayout.subHeight}
                  margin={{ top: CHART_TOP, right: RIGHT_MARGIN, bottom: 4, left: 0 }}
                  {...chartEvents}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  {xAxis(index === signals.length - 1)}
                  {yAxis(signal, true, index)}
                  {linesFor(signal, true)}
                  {zeroLineFor(signal, true)}
                  {minMaxFor(signal, true)}
                  {areasFor(signal, true)}
                </LineChart>
              </div>
            </div>
          ))
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              syncId="track"
              syncMethod="value"
              accessibilityLayer={false}
              margin={{ top: CHART_TOP, right: rightMargin, bottom: 4, left: 0 }}
              {...chartEvents}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              {xAxis(true)}
              {sharedScaleActive
                ? sharedYAxis
                : renderSignals.map((signal, index) => yAxis(signal, false, index))}
              {[...renderSignals].reverse().flatMap(signal => linesFor(signal, false))}
              {zeroLineFor(renderSignals[0], false)}
              {renderSignals.flatMap(signal => minMaxFor(signal, false))}
              {areasFor(renderSignals[0], false)}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
